import { Link } from 'react-router';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LOW_CREDITS_THRESHOLD } from '@wrapforce/shared/constants';
import { AlertTriangle, ArrowRight, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMe } from '@/features/session/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';
import { formatLeadVehicle } from '@/features/leads/vehicle';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { useDashboard } from './api';

const SOURCE_COLORS = ['var(--color-accent)', 'var(--color-info)', 'var(--color-success)', 'var(--color-warning)', 'var(--color-ink)', 'var(--color-muted)'];

const LEAD_SOURCE_STYLE: Record<string, { stripe: string; bg: string; text: string }> = {
  instagram: { stripe: '#8A3ECF', bg: '#F3EBFB', text: '#7A35BD' },
  website: { stripe: '#2456C4', bg: '#E8EEFB', text: '#1E4BB0' },
  whatsapp: { stripe: '#178246', bg: '#E4F5EB', text: '#136B3A' },
  walk_in: { stripe: '#767D88', bg: 'var(--color-surface-2)', text: 'var(--color-muted)' },
  referral: { stripe: '#767D88', bg: 'var(--color-surface-2)', text: 'var(--color-muted)' },
  phone: { stripe: '#767D88', bg: 'var(--color-surface-2)', text: 'var(--color-muted)' },
  other: { stripe: '#767D88', bg: 'var(--color-surface-2)', text: 'var(--color-muted)' },
};

export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;
  const { data, isLoading } = useDashboard(workspaceId, locationId);
  const { data: me } = useMe();

  if (isLoading || !data) return <div className="p-6 text-sm text-muted">{t('loading')}</div>;

  return data.role === 'installer' ? (
    <InstallerDashboard data={data} name={me?.fullName} />
  ) : (
    <StaffDashboard data={data} isOwner={membership?.role === 'owner'} />
  );
}

function InstallerDashboard({ data, name }: { data: Extract<import('@wrapforce/shared').DashboardResponse, { role: 'installer' }>; name?: string }) {
  const { t } = useTranslation('dashboard');
  const greeting =
    new Date().getHours() < 12 ? t('greeting.morning') : new Date().getHours() < 18 ? t('greeting.afternoon') : t('greeting.evening');
  return (
    <div className="flex flex-col gap-5 p-6">
      <h1 className="font-display text-xl font-bold text-ink">
        {greeting}
        {name ? t('greeting.nameSuffix', { name: name.split(' ')[0] }) : ''}
      </h1>

      <section className="flex flex-col gap-2.5">
        <h2 className="text-xs font-bold uppercase tracking-wide text-muted">{t('sections.today')}</h2>
        {data.myJobsToday.length === 0 ? <p className="text-sm text-muted">{t('empty.noJobsToday')}</p> : null}
        <div className="flex flex-col gap-2">
          {data.myJobsToday.map((j) => (
            <InstallerJobCard key={j.id} job={j} />
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2.5">
        <h2 className="text-xs font-bold uppercase tracking-wide text-muted">{t('sections.upcoming')}</h2>
        {data.myUpcoming.length === 0 ? <p className="text-sm text-muted">{t('empty.nothingElse')}</p> : null}
        <div className="flex flex-col gap-2">
          {data.myUpcoming.map((j) => (
            <InstallerJobCard key={j.id} job={j} />
          ))}
        </div>
      </section>
    </div>
  );
}

function InstallerJobCard({ job }: { job: import('@wrapforce/shared').JobListItem }) {
  const { t } = useTranslation('dashboard');
  const { formatDate } = useLocaleFormatters();
  return (
    <Link to={`/jobs/${job.id}`} className="flex items-center justify-between rounded-[var(--radius-card)] border border-border bg-surface p-3.5 hover:border-accent">
      <div>
        <div className="font-semibold text-ink">{job.vehicleLabel || job.customerName}</div>
        <div className="text-xs text-muted">{job.serviceSummary}</div>
        {job.scheduledStart ? <div className="mt-1 text-xs text-muted">{formatDate(new Date(job.scheduledStart), 'EEE h:mm a')}</div> : null}
      </div>
      <span className="bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase text-muted">{t(`jobStatus.${job.status}`)}</span>
    </Link>
  );
}

function StaffDashboard({ data, isOwner }: { data: Extract<import('@wrapforce/shared').DashboardResponse, { role: 'staff' }>; isOwner: boolean }) {
  const { t } = useTranslation('dashboard');
  const { formatMoney, formatDate, formatRelativeShort } = useLocaleFormatters();
  return (
    <div className="flex flex-col gap-5 p-6">
      {data.trialDaysLeft !== null ? (
        <div className={cn('rounded-[var(--radius-card)] border px-4 py-2.5 text-sm font-semibold', data.trialDaysLeft <= 3 ? 'border-warning/30 bg-warning/10 text-warning' : 'border-border bg-surface-2 text-muted')}>
          {t('trialBanner.daysLeft', { count: data.trialDaysLeft })} ·{' '}
          {isOwner ? (
            <Link to="/settings" className="underline">
              {t('trialBanner.upgrade')}
            </Link>
          ) : (
            <span>{t('trialBanner.askOwner')}</span>
          )}
        </div>
      ) : null}

      {data.creditsBalance <= LOW_CREDITS_THRESHOLD ? (
        <div className="flex items-center gap-2 rounded-[var(--radius-card)] border border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-warning">
          <AlertTriangle size={15} /> {t('lowCredits.message', { count: data.creditsBalance })}{' '}
          <Link to="/settings" className="font-semibold underline">
            {t('lowCredits.buyMore')}
          </Link>
        </div>
      ) : null}

      {data.outstandingBalance.valueCents > 0 ? (
        <Link
          to="/jobs?filter=unpaid"
          className="flex items-center gap-2 rounded-[var(--radius-card)] border border-danger/30 bg-danger/10 px-4 py-2.5 text-sm font-semibold text-danger hover:bg-danger/15"
        >
          <AlertTriangle size={15} className="shrink-0" />
          <span>{t('outstanding.banner', { money: formatMoney(data.outstandingBalance.valueCents), count: data.outstandingBalance.count })}</span>
          <span className="ml-auto flex items-center gap-1 whitespace-nowrap underline">
            {t('outstanding.action')} <ArrowRight size={12} />
          </span>
        </Link>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label={t('kpi.revenueMtd')} value={formatMoney(data.revenueMtdCents)} />
        <KpiCard label={t('kpi.activeLeads')} value={String(data.activeLeads)} />
        <KpiCard label={t('kpi.openQuotes')} value={String(data.openQuotes.count)} sub={formatMoney(data.openQuotes.valueCents)} />
        <KpiCard label={t('kpi.jobsThisWeek')} value={String(data.jobsThisWeek)} />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('charts.revenueTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenueLast6Months} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} barCategoryGap="28%">
                <defs>
                  <linearGradient id="revenueBarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={1} />
                    <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 4" />
                <XAxis dataKey="month" tickFormatter={(m: string) => formatDate(new Date(`${m}-02`), 'MMM')} tick={{ fontSize: 11, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[0, 'dataMax']} />
                <Tooltip
                  cursor={{ fill: 'var(--color-surface-2)', opacity: 0.6 }}
                  content={<ChartTooltip labelFormatter={(m) => formatDate(new Date(`${m}-02`), 'MMMM yyyy')} valueFormatter={(v) => formatMoney(v)} />}
                />
                <Bar dataKey="revenueCents" fill="url(#revenueBarFill)" radius={0} activeBar={{ fill: 'var(--color-accent-hover)' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('charts.leadsBySourceTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="h-56">
            {data.leadsBySource.length === 0 ? (
              <div className="grid h-full place-items-center text-sm text-muted">{t('empty.noLeads')}</div>
            ) : (
              <LeadsBySourceChart data={data.leadsBySource} />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('cards.recentLeads')}</CardTitle>
            <Link to="/leads" className="ml-auto flex items-center gap-1 text-xs font-semibold text-accent-ink">
              {t('actions.viewAll')} <ArrowRight size={12} />
            </Link>
          </CardHeader>
          <CardContent className="flex flex-col p-0">
            {data.recentLeads.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted">{t('empty.noLeads')}</p>
            ) : (
              data.recentLeads.map((l) => {
                const sourceKey = LEAD_SOURCE_STYLE[l.source] ? l.source : 'other';
                const style = LEAD_SOURCE_STYLE[sourceKey]!;
                return (
                  <Link key={l.id} to="/leads" className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0 hover:bg-surface-2">
                    <span className="h-[34px] w-1.5 flex-none" style={{ background: style.stripe }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink">{l.name}</div>
                      <div className="truncate text-xs text-muted">{formatLeadVehicle(l) || l.requirement}</div>
                    </div>
                    <span className="flex-none px-2 py-0.75 text-[10px] font-bold uppercase tracking-[.04em]" style={{ background: style.bg, color: style.text }}>
                      {t(`leadSource.${sourceKey}`)}
                    </span>
                    <span className="w-9 flex-none text-right font-mono text-[11px] text-muted">{formatRelativeShort(new Date(l.createdAt))}</span>
                  </Link>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('cards.upcomingJobs')}</CardTitle>
            <Link to="/calendar" className="ml-auto flex items-center gap-1 text-xs font-semibold text-accent-ink">
              {t('common:nav.calendar')} <ArrowRight size={12} />
            </Link>
          </CardHeader>
          <CardContent className="flex flex-col p-0">
            {data.todayJobs.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted">{t('empty.noJobsToday')}</p>
            ) : (
              data.todayJobs.map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0 hover:bg-surface-2">
                  <div className="flex-none bg-ink px-0 py-1 text-center text-surface" style={{ width: 44 }}>
                    <div className="text-[9px] font-bold uppercase tracking-[.1em] text-accent">
                      {j.scheduledStart ? formatDate(new Date(j.scheduledStart), 'EEE') : '—'}
                    </div>
                    <div className="font-mono text-[15px] font-semibold">
                      {j.scheduledStart ? new Date(j.scheduledStart).getDate() : '—'}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-ink">{j.vehicleLabel || j.customerName} · {j.serviceSummary}</div>
                    <div className="truncate text-xs text-muted">{j.installerNames.join(', ') || t('unassigned')} · {t(`jobStatus.${j.status}`)}</div>
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ListCard title={t('cards.awaitingDeposit')} emptyText={t('empty.noDeposits')} viewAllHref="/quotes">
          {data.awaitingDeposit.map((q) => (
            <Link key={q.id} to={`/quotes/${q.id}`} className="flex items-center justify-between px-2.5 py-2 hover:bg-surface-2">
              <div>
                <div className="text-sm font-semibold text-ink">Q-{q.number} · {q.customerName}</div>
                {q.sentAt ? <div className="text-xs text-muted">{t('deposit.sentOn', { date: formatDate(new Date(q.sentAt), 'P') })}</div> : null}
              </div>
              <span className="font-mono text-sm font-semibold">{formatMoney(q.depositCents)}</span>
            </Link>
          ))}
        </ListCard>

        <ListCard title={t('cards.lowStock')} emptyText={t('empty.stocked')} viewAllHref="/inventory">
          {data.lowStock.map((item) => (
            <Link key={item.id} to="/inventory" className="flex items-center justify-between px-2.5 py-2 hover:bg-surface-2">
              <div className="flex items-center gap-2">
                <Package size={14} className="text-muted" />
                <div>
                  <div className="text-sm font-semibold text-ink">{item.name}</div>
                  <div className="text-xs text-muted">{item.qtyOnHand} / {item.reorderPoint} {item.unit}</div>
                </div>
              </div>
              <span className={cn(' px-2 py-0.5 text-[10px] font-bold uppercase', item.status === 'out' ? 'bg-danger/15 text-danger' : 'bg-warning/15 text-warning')}>{t(`stockStatus.${item.status}`)}</span>
            </Link>
          ))}
        </ListCard>
      </div>
    </div>
  );
}

function LeadsBySourceChart({ data }: { data: { source: string; count: number }[] }) {
  const { t } = useTranslation('dashboard');
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const colorFor = (source: string, i: number) => LEAD_SOURCE_STYLE[source]?.stripe ?? SOURCE_COLORS[i % SOURCE_COLORS.length]!;
  return (
    <div className="flex h-full items-center gap-4">
      <div className="relative h-full flex-1 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="source" innerRadius="62%" outerRadius="92%" paddingAngle={2} stroke="var(--color-surface)" strokeWidth={2}>
              {data.map((entry, i) => (
                <Cell key={entry.source} fill={colorFor(entry.source, i)} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip nameFormatter={(name) => t(`leadSource.${LEAD_SOURCE_STYLE[name] ? name : 'other'}`)} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-[22px] font-semibold leading-none text-ink">{total}</span>
          <span className="mt-1 text-[9px] font-bold uppercase tracking-[.12em] text-muted">{t('charts.totalLeads')}</span>
        </div>
      </div>
      <ul className="flex w-[40%] flex-none flex-col gap-1.5">
        {data.map((entry, i) => {
          const sourceKey = LEAD_SOURCE_STYLE[entry.source] ? entry.source : 'other';
          return (
            <li key={entry.source} className="flex items-center gap-2 text-xs">
              <span className="h-2.5 w-2.5 flex-none" style={{ background: colorFor(entry.source, i) }} />
              <span className="min-w-0 flex-1 truncate font-semibold text-ink">{t(`leadSource.${sourceKey}`)}</span>
              <span className="font-mono text-muted">{entry.count}</span>
              <span className="w-9 text-right font-mono text-[11px] text-muted">{total > 0 ? Math.round((entry.count / total) * 100) : 0}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 p-4">
        <span className="text-[10px] font-semibold uppercase tracking-[.12em] text-muted">{label}</span>
        <span className="font-mono text-[26px] font-semibold text-ink">{value}</span>
        {sub ? <span className="text-[11.5px] text-muted">{sub}</span> : null}
      </CardContent>
    </Card>
  );
}

function ListCard({ title, emptyText, viewAllHref, children }: { title: string; emptyText: string; viewAllHref: string; children: React.ReactNode }) {
  const { t } = useTranslation('dashboard');
  const hasChildren = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Link to={viewAllHref} className="ml-auto flex items-center gap-1 text-xs font-semibold text-accent-ink">
          {t('actions.viewAll')} <ArrowRight size={12} />
        </Link>
      </CardHeader>
      <CardContent className="flex flex-col gap-0.5 p-2">
        {hasChildren ? children : <p className="px-2.5 py-3 text-sm text-muted">{emptyText}</p>}
      </CardContent>
    </Card>
  );
}
